import { Location, ViewportScroller } from '@angular/common';
import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    Breadcrumb,
    BreadcrumbAncestor,
    Cms,
    ICarDetails,
    TempAny
} from '@impact/data';
import { combineLatest, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { chevronLeft } from '../../icons';
import { HeaderService } from '../core/header.service';
import { sanitizeVehicleUrl } from '../core/helpers';
import { SettingsService, SiteIds } from '../core/settings.service';
import { FILTER_FACET } from '../filter-service/filter.models';
import {
    IActiveLeasingView,
    LEASING_PLAN_TYPES,
    LEASING_PURPOSE_TYPES,
    LeasingService,
} from '../leasing/leasing.service';
import { NavigationService } from '../navigation/navigation.service';
import { SidePanelService } from '../side-panel/side-panel.service';
import { FeatureDetectionService } from '../utils/helpers/feature-detection.service';
import { UrlifyPipe } from '../utils/urlify.pipe';
import {
    IUsedCarPaymentOptions,
    IUsedCarSpecifications,
    UsedCarPageSectionsMotorpoint,
    UsedCarSpecTypes,
} from './used-car.models';
import { UsedCarService } from './used-car.service';

@Component({
    selector: 'app-used-car-details-motorpoint',
    template: `
        <div #galleryRef [id]="UsedCarPageSections.Gallery" class="used-car-hero__wrapper">
            <app-used-car-hero-motorpoint [car]="carDetails"></app-used-car-hero-motorpoint>
        </div>
        <div
            class="scroll-sticky-wrapper used-car-menu"
            #stickyScrollRef
            [ngStyle]="{ height: scrollElementHeight }"
            [ngClass]="{ 'scroll-sticky-wrapper--is-sticky': isFixed }"
            >
            <div class="page-navigation-header used-car-details__page-navigation-header" [style.top]="moveMenuDown ? headerHeight + 'px' : ''">
                <div class="page-navigation-header__wrapper">
                    <div class="page-navigation-header__column-left">
                        <div class="used-car-menu__heading">{{ carDetails?.make?.value }} {{ carDetails?.model?.value }}</div>
                        <div class="used-car-menu__sub-heading">
                            {{ carDetails?.variant?.value }}
                        </div>
                    </div>

                    <div class="page-navigation-header__column-center">
                        <nav class="used-car-menu-nav">
                            <ul class="used-car-menu-nav__list" #listElements>
                                <li class="used-car-menu-nav__list-item" #listElement>
                                    <button
                                        class="page-secondary-navigation__item-link"
                                        [ngClass]="{ 'is-active': activeScrollMenuItem === UsedCarPageSections.Gallery }"
                                        (click)="scrollToSection(UsedCarPageSections.Gallery)"
                                    >
                                        <span class="page-secondary-navigation__item-text">
                                            {{ 'used_car_details.menu_gallery' | translate }}
                                        </span>
                                    </button>
                                </li>
                                <li class="used-car-menu-nav__list-item" #listElement>
                                    <button
                                        class="page-secondary-navigation__item-link"
                                        [ngClass]="{ 'is-active': activeScrollMenuItem === UsedCarPageSections.Details }"
                                        (click)="scrollToSection(UsedCarPageSections.Details)"
                                    >
                                        <span class="page-secondary-navigation__item-text">
                                            {{ 'used_car_details.menu_details' | translate }}
                                        </span>
                                    </button>
                                </li>
                                <li class="used-car-menu-nav__list-item" #listElement>
                                    <button
                                        class="page-secondary-navigation__item-link"
                                        [ngClass]="{ 'is-active': activeScrollMenuItem === UsedCarPageSections.Equipment }"
                                        (click)="scrollToSection(UsedCarPageSections.Equipment)"
                                    >
                                        <span class="page-secondary-navigation__item-text">
                                            {{ 'used_car_details.equipment' | translate }}
                                        </span>
                                    </button>
                                </li>
                                <li class="used-car-menu-nav__list-item" #listElement>
                                    <button
                                        class="page-secondary-navigation__item-link"
                                        [ngClass]="{ 'is-active': activeScrollMenuItem === UsedCarPageSections.Specifications }"
                                        (click)="scrollToSection(UsedCarPageSections.Specifications)"
                                    >
                                        <span class="page-secondary-navigation__item-text">
                                            {{ 'used_car_details.specifications_heading' | translate }}
                                        </span>
                                    </button>
                                </li>
                                <li class="used-car-menu-nav__list-item" #listElement>
                                    <button
                                        class="page-secondary-navigation__item-link"
                                        [ngClass]="{ 'is-active': activeScrollMenuItem === UsedCarPageSections.Financing }"
                                        (click)="scrollToSection(UsedCarPageSections.Financing)"
                                    >
                                        <span class="page-secondary-navigation__item-text">
                                            {{ 'used_car_details.menu_financing' | translate }}
                                        </span>
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>

                    <ng-container *ngIf="carDetails?.santanderPaymentPerMonth as santanderPaymentPerMonth">
                        <div
                            class="page-navigation-header__column-right"
                            [class.page-navigation-header__column-right--small]="!santanderPaymentPerMonth.data"
                        >
                            <div class="page-navigation-header__price">
                                <div class="used-car-menu__model">{{ carDetails?.make?.value }} {{ carDetails?.model?.value }}</div>
                                <div class="used-car-menu__price-wrapper">
                                    <div class="primary-price" [ngSwitch]="currentLeasingPrice !== undefined && currentLeasingPrice >= 1000">
                                        <ng-container *ngSwitchCase="true">{{ currentLeasingPrice | number: '1.0-0' }}  {{ leasingDataTexts?.general?.costSuffixText }}</ng-container>
                                        <ng-container *ngSwitchCase="false">{{ 'generic.call_for_price' | translate }}</ng-container>
                                    </div>
                                    <div class="secondary-price">
                                        <button class="button--as-link" [ngSwitch]="activeLeasingPurpose" (click)="scrollToSection(UsedCarPageSections.Financing)">
                                            <span *ngSwitchCase="leasingServiceTypes.PRIVATE">
                                                {{ 'leasing.leasing_service_vat_private' | translate }}
                                            </span>
                                            <span *ngSwitchCase="leasingServiceTypes.BUSINESS">
                                                {{ 'leasing.leasing_service_vat_business' | translate }}
                                            </span>
                                            <div class="button--mini">i</div>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <button
                                class="button button--primary page-navigation-header__price-button button--themed"
                                (click)="scrollToSection(UsedCarPageSections.Financing)"
                            >
                                {{ 'used_car_details.buy_the_car' | translate }}
                            </button>
                        </div>
                    </ng-container>
                </div>
            </div>
        </div>

        <div #detailsRef [id]="UsedCarPageSections.Details">
            <!-- Breadcrumb -->
            <nav role="navigation" class="used-car-details__breadcrumbs-wrapper">
                <ol class="used-car-details__breadcrumbs-list" *ngIf="breadcrumbData?.breadcrumbPath">
                    <li class="used-car-details__breadcrumbs-list-item">
                        <a class="used-car-details__breadcrumbs-link"
                            [routerLink]="breadcrumbData.currentPage?.url">
                            <span class="used-car-details__breadcrumbs-link-icon">${chevronLeft}</span>
                            {{ 'generic.all_prefix_text' | translate }} {{ carDetails?.make?.value }}
                            <ng-container *ngIf="breadcrumbData?.hasModel">{{ carDetails?.model?.value }}</ng-container>
                        </a>
                    </li>
                    <li class="used-car-details__breadcrumbs-list-item" *ngFor="let item of breadcrumbData.breadcrumbPath; let i = index">
                        <a class="used-car-details__breadcrumbs-link"
                            [routerLink]="item.url">
                            {{ item.name }}
                        </a>
                    </li>
                </ol>
            </nav>

            <!-- MAIN -->
            <div class="layout-wrapper">
                <app-used-car-main-details></app-used-car-main-details>
            </div>

            <div #financingRef [id]="UsedCarPageSections.Financing">
                <app-used-car-financing-motorpoint
                    [carData]="carDetails"
                    [leasingDataTexts]="leasingDataTexts"
                    [pageData]="pageData">
                </app-used-car-financing-motorpoint>
            </div>

            <app-usp class="used-car-details-usp" [items]="pageData?.usp"></app-usp>
        </div>

        <div #equipmentRef [id]="UsedCarPageSections.Equipment" class="layout-wrapper">
            <app-used-car-selected-equipment
                [isMotorpoint]="site === siteIds.MOTORPOINT"
                [equipment]="carDetails?.equipmentList"
            ></app-used-car-selected-equipment>
        </div>
        <div #specificationsRef [id]="UsedCarPageSections.Specifications" *ngIf="specifications">
            <app-used-car-specifications [isMotorpoint]="true" [specifications]="specifications"></app-used-car-specifications>
        </div>

        <div class="book-test-drive-cta-wrapper--motorpoint">
            <div class="book-test-drive-cta-wrapper">
                <app-book-used-car-test-drive-cta [data]="bookTestDriveCtaData"></app-book-used-car-test-drive-cta>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsedCarDetailsMotorpointComponent implements OnInit, OnDestroy, AfterViewInit, AfterContentChecked {
    @ViewChild('stickyScrollRef') stickyScrollRef: ElementRef;
    @ViewChildren('listElement') listElements: QueryList<ElementRef>;
    @ViewChild('galleryRef') galleryRef: ElementRef;
    @ViewChild('detailsRef') detailsRef: ElementRef;
    @ViewChild('equipmentRef') equipmentRef: ElementRef;
    @ViewChild('specificationsRef') specificationsRef: ElementRef;
    @ViewChild('financingRef') financingRef: ElementRef;
    @Input() carDetails: ICarDetails;
    @Input() pageData: Cms.UsedCarDetailsPage;

    paymentOptions: IUsedCarPaymentOptions;
    specifications: IUsedCarSpecifications;
    usedCarDetailsLinkData: TempAny;
    usedCarsOverviewPage: TempAny;

    usedCarsVehicleMakesPage: TempAny;
    breadcrumbData: TempAny;

    site: string;
    siteIds = SiteIds;

    UsedCarPageSections = UsedCarPageSectionsMotorpoint;
    deactivateScrollTrigger: boolean;
    listElementsArray: TempAny[];
    sectionsScrollOffset: number;

    galleryElementYPosition: number;
    galleryElementHeight: number;

    detailsElementYPosition: number;
    detailsElementHeight: number;

    equipmentsElementYPosition: number;
    equipmentsElementHeight: number;

    specificationsElementYPosition: number;
    specificationsElementHeight: number;

    financingElementYPosition: number;
    financingElementHeight: number;

    activeScrollMenuItem: UsedCarPageSectionsMotorpoint | undefined;

    bookTestDriveCtaData: Cms.BookUsedCarTestDriveCtaSpot;

    currentLeasingPrice: string | number;

    // Leasing
    leasingDataTexts: TempAny;
    leasingServiceTypes = LEASING_PURPOSE_TYPES;
    LeasingPlanTypes = LEASING_PLAN_TYPES;
    activeLeasingPurpose: string;

    ///
    /// Sticky menu properties
    ///
    scrollElement: HTMLElement;
    scrollElementHeight: string;
    stickyScrollElementYPosition: number;
    isFixed: boolean;
    moveMenuDown: boolean;
    headerHeight: number;
    prevScrollPosition: number;

    private unsubscribe = new Subject<void>();

    @HostListener('window:scroll')
    onWindowScroll() {
        if (this.featureDetectionService.isBrowser()) {
            if (this.deactivateScrollTrigger) {
                return;
            }

            // Gallery section
            if (window.scrollY === 0 || window.scrollY < this.detailsElementYPosition) {
                this.activeScrollMenuItem = this.UsedCarPageSections.Gallery;
            }

            // Details section
            if (
                window.scrollY !== 0 &&
                window.scrollY > this.elmentScrollTriggerPosition(this.galleryElementYPosition, this.galleryElementHeight)
            ) {
                this.activeScrollMenuItem = this.UsedCarPageSections.Details;
            }

            // Equipments section
            if (window.scrollY > this.elmentScrollTriggerPosition(this.detailsElementYPosition, this.detailsElementHeight)) {
                this.activeScrollMenuItem = this.UsedCarPageSections.Equipment;
            }

            // Specifications section
            if (window.scrollY > this.elmentScrollTriggerPosition(this.equipmentsElementYPosition, this.equipmentsElementHeight)) {
                this.activeScrollMenuItem = this.UsedCarPageSections.Specifications;
            }

            // Financing section
            if (window.scrollY > this.elmentScrollTriggerPosition(this.specificationsElementYPosition, this.specificationsElementHeight)) {
                this.activeScrollMenuItem = this.UsedCarPageSections.Financing;
            }

            // Below sections
            if (window.scrollY >= this.elmentScrollTriggerPosition(this.financingElementYPosition, this.financingElementHeight)) {
                this.activeScrollMenuItem = undefined;
            }
        }
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private featureDetectionService: FeatureDetectionService,
        private readonly headerService: HeaderService,
        private route: ActivatedRoute,
        private scroller: ViewportScroller,
        private settingsService: SettingsService,
        private readonly navigationService: NavigationService,
        private sidePanelService: SidePanelService,
        private usedCarService: UsedCarService,
        public location: Location,
        private leasingService: LeasingService
    ) {
        this.activeScrollMenuItem = this.UsedCarPageSections.Gallery;

        this.featureDetectionService.onResize$.pipe(takeUntil(this.unsubscribe)).subscribe((viewport) => {
            if (viewport && this.stickyScrollRef) {
                this.stickyScrollElementYPosition = this.stickyScrollRef.nativeElement.getBoundingClientRect().top + window.scrollY;
            }
        });

        this.leasingService.getLeasingGlobalData().pipe(take(1)).subscribe((umbracoLeasingData: any) => {
            this.leasingDataTexts = umbracoLeasingData?.texts;
        });

        this.leasingService.getActiveLeasingToggles().pipe(takeUntil(this.unsubscribe)).subscribe((leasingToggles: IActiveLeasingView | undefined) => {
            if (leasingToggles) {
                this.activeLeasingPurpose = leasingToggles.leasingPurpose ?? '';
            }
        });

        // Listens for changes in leasing price, altered by the leasing configurator
        this.leasingService.currentUsedCarLeasingPrice$.pipe(takeUntil(this.unsubscribe)).subscribe((price: string | number) => {
            this.currentLeasingPrice = price;
        });
    }

    ngOnInit(): void {
        this.scroller.setOffset([0, 150]);

        // Fetch Car data, then leasing data
        if (this.carDetails) {
            combineLatest([
                this.settingsService.getGlobalPages(),
                this.settingsService.getSiteId(),
                this.navigationService.usedCarsNavigation$
            ]).pipe(takeUntil(this.unsubscribe))
            .subscribe(([globalPages, siteId, usedCarsNavigation]) => {
                this.site = siteId;
                this.usedCarsOverviewPage = globalPages.usedCarsOverviewPage;
                this.usedCarsVehicleMakesPage = globalPages.usedCarsVehicleMakesPage;

                if (globalPages.usedCarsVehicleMakesPage) {
                    const sanitizedUrl = sanitizeVehicleUrl(`${this.usedCarsVehicleMakesPage.url}/${this.carDetails?.make?.value}/${this.carDetails?.model?.value}`);
                    this.generateBreadCrumb(sanitizedUrl, usedCarsNavigation);
                } else {
                    console.warn('[BN: Missing usedCarsVehicleMakesPage from umbraco settings]');
                }
            });
        }

        // Format details text
        if (this.carDetails?.comment?.value) {
            const commentRegex = new RegExp(/(?:\\[rn]|[\r\n]+)+/g);
            this.carDetails.comment.value = this.carDetails?.comment?.value?.replace(commentRegex, '<br>');
        }

        this.usedCarDetailsLinkData = {
            usedCarsOverviewPageUrl: `/${this.route?.snapshot?.url[0]?.path}`,
            makeParamKey: FILTER_FACET.MAKE,
            makeParamValue: new UrlifyPipe().transform(this.carDetails?.make?.value ?? ''),
            makeParamText: this.carDetails?.make?.value,
            modelParamKey: FILTER_FACET.MODEL,
            modelParamValue: new UrlifyPipe().transform(this.carDetails?.model?.value ?? ''),
            modelParamText: this.carDetails?.model?.value,
        };

        this.bookTestDriveCtaData = {
            bookTestDriveCtaCategoryHeaderText: this.pageData?.bookTestDriveCtaCategoryHeaderText,
            bookTestDriveCtaHeaderText: this.pageData?.bookTestDriveCtaHeaderText,
            bookTestDriveCtaText: this.pageData?.bookTestDriveCtaText,
            id: this.pageData?.id,
            name: this.pageData?.name,
            type: this.pageData?.type,
        };

        this.usedCarService.setCarDetails(this.carDetails);
        this.usedCarService.setPageData(this.pageData);
        this.paymentOptions = {
            cash: { ...this.carDetails?.cashPrice, vatIncluded: this.carDetails?.priceIncludesVat?.data },
            loanPerMonth: this.carDetails?.santanderPaymentPerMonth,
            loanDownPayment: this.carDetails?.santanderDownPayment,
        };

        if (this.featureDetectionService.isBrowser()) {
            this.sectionsScrollOffset = window.innerHeight / 2;
            this.scrollToSection(this.UsedCarPageSections.Gallery);
        }

        this.specifications = {
            main: {
                acceleration: this.carDetails?.acceleration0To100,
                kmPerLiter: this.carDetails?.kmPerLiter,
                topSpeed: this.carDetails?.topSpeed,
            },
            allSpecifications: this.carDetails?.specificationGroups
                ? Object.entries(this.carDetails?.specificationGroups).map(([key, value]) => ({
                      ...value,
                      type: key as UsedCarSpecTypes,
                  }))
                : undefined,
            disclaimerText: this.pageData?.disclaimerSpecificationsText,
        };

        // Sticky header:
        this.headerService
            .getScrollPosition()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((position: number) => {
                this.handleModelHeaderPosition(position);
            });

        this.headerService
            .getHeaderEvents()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((height: number) => {
                this.headerHeight = height;
                this.prevScrollPosition = height;
                this.cdr.markForCheck();
            });
    }

    ngAfterViewInit(): void {
        if (this.listElements) {
            this.listElementsArray = this.listElements.toArray();
        }

        if (this.stickyScrollRef) {
            this.scrollElement = this.stickyScrollRef.nativeElement;
            if (this.stickyScrollRef.nativeElement.getBoundingClientRect) {
                this.stickyScrollElementYPosition = this.stickyScrollRef.nativeElement.getBoundingClientRect()?.top + window.scrollY;
            }
        }
    }

    ngAfterContentChecked(): void {
        if (this.featureDetectionService.isBrowser()) {
            this.calculateSections();
        }
    }

    generateBreadCrumb(url: string, navigationData: Cms.NavigationDto[], hasModel = true) {
        const searchResult = this.breadcrumb(navigationData, url);

        if (searchResult) {
            searchResult.breadcrumbPath.unshift(this.usedCarsOverviewPage);
            searchResult.hasModel = hasModel;
            this.breadcrumbData = searchResult;
            this.cdr.markForCheck();
        } else {
            // Backup breadcrumb based on make if model page is not found in umbraco navigation data
            const sanitizedUrl = sanitizeVehicleUrl(`${this.usedCarsVehicleMakesPage.url}/${this.carDetails?.make?.value}`);
            this.generateBreadCrumb(sanitizedUrl, navigationData, false);
        }
    }

    private findPageRecursive(currentPage: Cms.NavigationDto, targetUrl: string, parentInfo: BreadcrumbAncestor[] = []): Breadcrumb | undefined {
        const currentUrl = currentPage.url;
        const currentName = currentPage.name || '';
        const updatedParents = [...parentInfo, { url: currentUrl, name: currentName }];

        if (currentUrl === targetUrl) {
            return { currentPage, breadcrumbPath: updatedParents };
        }

        if (currentPage.children && currentPage.children.length > 0) {
            for (let i = 0; i < currentPage.children.length; i++) {
                const child = currentPage.children[i];
                const childResult = this.findPageRecursive(child, targetUrl, updatedParents);

                if (childResult) {
                    return { ...childResult, index: i };
                }
            }
        }

        return undefined;
    }

    breadcrumb(pages: Cms.NavigationDto[], targetUrl: string) {
        for (let i = 0; i < pages.length; i++) {
            const page = pages[i];
            const result = this.findPageRecursive(page, targetUrl);

            if (result) {
                return result;
            }
        }

        return undefined;
    }

    handleModelHeaderPosition(position: number) {
        if (window.scrollY < this.stickyScrollElementYPosition - this.headerHeight) {
            this.isFixed = false;
            this.moveMenuDown = false;
            this.scrollElementHeight = 'auto';
        } else {
            this.moveMenuDown = position < this.prevScrollPosition;
        }
        if (window.scrollY >= this.stickyScrollElementYPosition) {
            this.isFixed = true;
            this.scrollElementHeight = `${this.scrollElement.clientHeight}px`;
        }
        this.prevScrollPosition = position;
        this.cdr.markForCheck();
    }

    scrollToSection(anchor: UsedCarPageSectionsMotorpoint) {
        if (this.featureDetectionService.isBrowser()) {
            if (anchor === this.UsedCarPageSections.Gallery) {
                this.scroller.scrollToPosition([0, 0]);
            } else {
                this.deactivateScrollTrigger = true;
                const index = this.getActiveAnchorIndex(anchor);
                this.scrollToListItem(index);
                this.scroller.scrollToAnchor(anchor);
                this.activeScrollMenuItem = anchor;
                this.cdr.markForCheck();

                const scrollerTimeout = setTimeout(() => {
                    this.deactivateScrollTrigger = false;
                    clearTimeout(scrollerTimeout);
                    this.cdr.markForCheck();
                }, 500);
            }
        }
    }

    scrollToListItem(index: number): void {
        if (!this.listElements && index !== -1) {
            return;
        }

        if (this.featureDetectionService.isBrowser()) {
            const element: HTMLElement = this.listElementsArray[index].nativeElement;
            const parentElement = element.parentElement;

            if (parentElement) {
                let scrollDistance: number;

                if (index + 2 === this.listElementsArray.length) {
                    scrollDistance = parentElement.offsetWidth;
                } else {
                    scrollDistance = element.offsetLeft - parentElement?.scrollLeft - parentElement.offsetWidth / 2 + element.offsetWidth;
                }

                parentElement?.scrollBy({
                    top: 0,
                    left: scrollDistance,
                    behavior: 'smooth',
                });
            }
        }
    }

    getActiveAnchorIndex(anchor: UsedCarPageSectionsMotorpoint): number {
        switch (anchor) {
            case this.UsedCarPageSections.Gallery:
                return 0;
            case this.UsedCarPageSections.Details:
                return 1;
            case this.UsedCarPageSections.Equipment:
                return 2;
            case this.UsedCarPageSections.Specifications:
                return 3;
            case this.UsedCarPageSections.Financing:
                return 4;
            default:
                return 0;
        }
    }

    calculateSections() {
        if (this.galleryRef) {
            const galleryElement = this.galleryRef.nativeElement;
            this.galleryElementYPosition = this.elementYPosition(galleryElement);
            this.galleryElementHeight = this.elementHeight(galleryElement);
        }

        if (this.detailsRef) {
            const detailsElement = this.detailsRef.nativeElement;
            this.detailsElementYPosition = this.elementYPosition(detailsElement);
            this.detailsElementHeight = this.elementHeight(detailsElement);
        }

        if (this.equipmentRef) {
            const equipmentsElement = this.equipmentRef.nativeElement;
            this.equipmentsElementYPosition = this.elementYPosition(equipmentsElement);
            this.equipmentsElementHeight = this.elementHeight(equipmentsElement);
        }

        if (this.specificationsRef) {
            const specificationsElement = this.specificationsRef.nativeElement;
            this.specificationsElementYPosition = this.elementYPosition(specificationsElement);
            this.specificationsElementHeight = this.elementHeight(specificationsElement);
        }

        if (this.financingRef) {
            const financingElement = this.financingRef.nativeElement;
            this.financingElementYPosition = this.elementYPosition(financingElement);
            this.financingElementHeight = this.elementHeight(financingElement);
        }
    }

    elmentScrollTriggerPosition(yPosition: number, height: number): number {
        return yPosition + height - this.sectionsScrollOffset;
    }

    elementYPosition(element: HTMLElement): number {
        return element.getBoundingClientRect().top + window.scrollY;
    }

    elementHeight(element: HTMLElement): number {
        return element.clientHeight > window.innerHeight / 2 ? element.clientHeight : window.innerHeight / 2;
    }

    showLoanSimulator(price?: number): void {
        if (price) {
            this.sidePanelService.showLoanCalculatorSidePanel({
                objectPrice: price,
                registrationDate: this.carDetails?.registrationDate?.data ?? undefined,
            });
        }
    }

    scrollToFinancing() {
        this.scroller.scrollToAnchor(this.UsedCarPageSections.Financing);
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
